import { Building2 } from 'lucide-react';
import * as React from 'react';
import { Button, Checkbox, EmptyState, ErrorState, LoadingState } from '@payment-plan/ui';
import { EYEBROW_CLASS, NATIVE_SELECT_CLASS, SECTION_CARD_CLASS } from './field-styles';
import type { AvailableUnit, FlowDraft, UnitState, UnitType } from '../../../types/separations';
import { mensajeDeError } from '../errors';
import { formatArea, formatCOP, toPesos } from '../money';
import { useAvailableProjects, useAvailableUnits, useCreateSeparation } from '../use-separation-flow';

/** Tipos que solo pueden ir como unidad adicional (HU-C01 CA-3; `separations.service.ts`). */
const TIPOS_ADICIONALES: UnitType[] = ['PARQUEADERO', 'DEPOSITO'];
export const MAX_ADICIONALES = 3;

const TIPO_LABEL: Record<UnitType, string> = {
  APARTAMENTO: 'Apartamento',
  CASA: 'Casa',
  LOCAL: 'Local',
  PARQUEADERO: 'Parqueadero',
  DEPOSITO: 'Depósito',
};

/** fix-50 · H-08 (HU-C01 CA-2): las no disponibles se muestran marcadas y no se pueden elegir. */
const ESTADO_MARCA: Record<Exclude<UnitState, 'DISPONIBLE'>, string> = {
  SEPARADA: 'separada',
  VENDIDA: 'vendida',
};
const disponible = (u: AvailableUnit): boolean => u.estado === 'DISPONIBLE';
const marca = (u: AvailableUnit): string | null => (disponible(u) ? null : ESTADO_MARCA[u.estado as Exclude<UnitState, 'DISPONIBLE'>]);

const collator = new Intl.Collator('es', { numeric: true });
const ordenar = (a: string, b: string): number => collator.compare(a, b);

interface UnitStepProps {
  onCreated: (draft: FlowDraft) => void;
}

export function UnitStep({ onCreated }: UnitStepProps): React.JSX.Element {
  const proyectos = useAvailableProjects();
  const [proyectoId, setProyectoId] = React.useState('');
  const [torre, setTorre] = React.useState('');
  const [unidadId, setUnidadId] = React.useState('');
  const [adicionalesIds, setAdicionalesIds] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const unidades = useAvailableUnits(proyectoId);
  const crear = useCreateSeparation();

  const todas = unidades.data ?? [];
  const principales = todas.filter((u) => !TIPOS_ADICIONALES.includes(u.tipo));
  const adicionales = todas
    .filter((u) => TIPOS_ADICIONALES.includes(u.tipo))
    .sort((a, b) => ordenar(a.numero, b.numero));
  const torres = Array.from(new Set(principales.map((u) => u.torre))).sort(ordenar);
  const hayDisponibles = principales.some(disponible);
  const unidadesTorre = principales.filter((u) => u.torre === torre).sort((a, b) => ordenar(a.numero, b.numero));
  const unidad = principales.find((u) => u.id === unidadId && disponible(u));
  const proyecto = proyectos.data?.find((p) => p.id === proyectoId);
  const limite = adicionalesIds.length >= MAX_ADICIONALES;

  const elegirProyecto = (id: string): void => {
    setProyectoId(id);
    setTorre('');
    setUnidadId('');
    setAdicionalesIds([]);
    setError(null);
  };

  const alternarAdicional = (id: string, marcado: boolean): void => {
    setAdicionalesIds((actual) =>
      marcado ? (actual.length >= MAX_ADICIONALES ? actual : [...actual, id]) : actual.filter((x) => x !== id),
    );
  };

  const iniciar = (): void => {
    if (!unidad || !proyecto) return;
    setError(null);
    crear.mutate(
      { proyectoId, unidadId: unidad.id, adicionalesIds },
      {
        onSuccess: (borrador) =>
          onCreated({
            id: borrador.id,
            precioTotal: toPesos(borrador.precioTotal),
            proyecto,
            unidad,
            adicionales: adicionales.filter((u) => adicionalesIds.includes(u.id)),
          }),
        onError: (e) => setError(mensajeDeError(e, 'iniciar la separación')),
      },
    );
  };

  if (proyectos.isLoading) return <LoadingState message="Cargando proyectos" />;
  if (proyectos.isError) {
    return <ErrorState message="No fue posible cargar tus proyectos." onRetry={() => void proyectos.refetch()} />;
  }
  if ((proyectos.data ?? []).length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
        title="No tienes proyectos activos asignados"
        description="Pide al administrador de tu inmobiliaria que te asigne un proyecto con catálogo."
      />
    );
  }

  return (
    <section aria-labelledby="paso-unidad" className={SECTION_CARD_CLASS}>
      <div>
        <p className={EYEBROW_CLASS}>Paso 1</p>
        <h2 id="paso-unidad" className="text-xl font-semibold tracking-tight text-foreground">
          Unidad a separar
        </h2>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-1.5">
          <label htmlFor="sep-proyecto" className="block text-sm font-medium text-foreground">
            Proyecto
          </label>
          <select
            id="sep-proyecto"
            className={NATIVE_SELECT_CLASS}
            value={proyectoId}
            onChange={(e) => elegirProyecto(e.target.value)}
          >
            <option value="">Selecciona un proyecto</option>
            {(proyectos.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </div>

        {proyectoId && !unidades.isLoading && hayDisponibles && (
          <>
            <div className="space-y-1.5">
              <label htmlFor="sep-torre" className="block text-sm font-medium text-foreground">
                Torre
              </label>
              <select
                id="sep-torre"
                className={NATIVE_SELECT_CLASS}
                value={torre}
                onChange={(e) => {
                  setTorre(e.target.value);
                  setUnidadId('');
                }}
              >
                <option value="">Selecciona la torre</option>
                {torres.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="sep-unidad" className="block text-sm font-medium text-foreground">
                Unidad
              </label>
              <select
                id="sep-unidad"
                className={NATIVE_SELECT_CLASS}
                value={unidadId}
                disabled={!torre}
                onChange={(e) => setUnidadId(e.target.value)}
              >
                <option value="">Selecciona la unidad</option>
                {unidadesTorre.map((u) => (
                  <option key={u.id} value={u.id} disabled={!disponible(u)}>
                    {u.numero} · {TIPO_LABEL[u.tipo]}
                    {marca(u) ? ` · ${marca(u)}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {proyectoId && unidades.isLoading && <LoadingState size="sm" message="Cargando unidades disponibles" />}
      {proyectoId && unidades.isError && (
        <ErrorState message="No fue posible cargar las unidades." onRetry={() => void unidades.refetch()} />
      )}
      {proyectoId && unidades.isSuccess && !hayDisponibles && (
        <EmptyState
          title="No hay unidades disponibles"
          description="Todas las unidades de este proyecto están separadas o vendidas."
        />
      )}
      {proyectoId && unidades.isSuccess && hayDisponibles && (
        <p className="text-xs text-muted-foreground">
          Las unidades separadas o vendidas aparecen marcadas y no se pueden elegir.
        </p>
      )}

      {unidad && <UnitSheet unidad={unidad} />}

      {unidad && adicionales.length > 0 && (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-foreground">
            Unidades adicionales <span className="font-normal text-muted-foreground">(parqueaderos o depósitos)</span>
          </legend>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {adicionalesIds.length} de {MAX_ADICIONALES} adicionales
          </p>
          <ul className="grid gap-2 lg:grid-cols-2">
            {adicionales.map((u) => {
              const marcado = adicionalesIds.includes(u.id);
              const id = `adicional-${u.id}`;
              const estado = marca(u);
              return (
                <li key={u.id}>
                  <label
                    htmlFor={id}
                    className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-border/70 px-3 py-2 transition-colors hover:bg-muted/30 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
                  >
                    <Checkbox
                      id={id}
                      checked={marcado}
                      disabled={estado !== null || (!marcado && limite)}
                      onCheckedChange={(v) => alternarAdicional(u.id, v === true)}
                    />
                    <span className="flex-1 text-sm">
                      <span className="font-medium">{u.numero}</span> · {TIPO_LABEL[u.tipo]}
                      {estado && (
                        <span className="ml-2 rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {estado.charAt(0).toUpperCase() + estado.slice(1)}
                        </span>
                      )}
                    </span>
                    <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">{formatCOP(u.precioLista)}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button size="lg" className="w-full lg:w-auto" disabled={!unidad} loading={crear.isPending} onClick={iniciar}>
          Iniciar separación
        </Button>
      </div>
    </section>
  );
}

function UnitSheet({ unidad }: { unidad: AvailableUnit }): React.JSX.Element {
  const datos: Array<[string, string]> = [
    ['Tipo', TIPO_LABEL[unidad.tipo]],
    ['Área privada', formatArea(unidad.areaPrivada)],
    ['Área construida', formatArea(unidad.areaConstruida)],
    ['Matrícula inmobiliaria', unidad.matricula],
    ['Precio de lista', formatCOP(unidad.precioLista)],
  ];
  return (
    <section aria-label="Datos de la unidad" className="rounded-lg border border-border/70 bg-surface-fade p-4">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-5">
        {datos.map(([dt, dd]) => (
          <div key={dt}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dt}</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-foreground">{dd}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
